import React, { useState, useEffect } from 'react';
import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  NavbarButton,
  MobileNavHeader,
  MobileNavToggle,
  MobileNavMenu,
} from '@/components/ui/resizable-navbar';
import { motion } from 'motion/react';
import { cn } from '@/lib/utils';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  const navItems = [
    {
      name: '회사 소개',
      link: '#about',
    },
    {
      name: '서비스',
      link: '#services',
    },
    {
      name: '포트폴리오',
      link: '#portfolio',
    },
    {
      name: '블로그',
      link: '/blog',
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['about', 'services', 'portfolio', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }}
      className="fixed top-5 left-0 right-0 z-50 px-4"
    >
    <Navbar className="relative overflow-hidden rounded-2xl">
      {/* Liquid glass effect overlay */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute inset-0 liquid-glow"
          style={{
            background: 'radial-gradient(circle at 30% 50%, rgba(255, 255, 255, 0.08) 0%, transparent 60%)',
            filter: 'blur(20px)'
          }}
        />
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 70% 50%, rgba(128, 90, 213, 0.05) 0%, transparent 60%)',
            filter: 'blur(30px)',
            animation: 'liquid-glow 12s ease-in-out infinite reverse'
          }}
        />
      </div>
      {/* Desktop Navigation */}
      <NavBody className="!min-w-0 !w-full !mx-auto !max-w-5xl !rounded-2xl !px-3 !py-3">
        <a href="/" className="relative z-30 flex items-center">
          <span className="text-base font-semibold text-white">
            Nalda<span className="text-zinc-500">Dev</span>
          </span>
        </a>
        
        <div className="flex items-center gap-1 relative z-20">
          {navItems.map((item, idx) => (
            <a
              key={idx}
              href={item.link}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-full transition-all",
                "hover:bg-zinc-900 hover:text-white",
                activeSection === item.link.slice(1)
                  ? "text-white bg-zinc-900"
                  : "text-zinc-400"
              )}
            >
              {item.name}
            </a>
          ))}
        </div>
        
        <div className="flex items-center gap-3 relative z-20">
          <a
            href="http://pf.kakao.com/_XSxhNn/chat"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 text-sm font-medium text-zinc-400 hover:text-[#FEE500] transition-all flex items-center gap-2"
          >
            <svg className="w-4 h-4" viewBox="0 0 256 256" fill="currentColor">
              <path d="M128 36C70.562 36 24 72.713 24 118c0 29.279 19.466 54.97 48.748 69.477l-10.534 38.883c-.905 3.34 2.998 5.99 5.756 3.905l46.725-33.452C120.077 197.905 125.975 198.5 128 198.5c57.438 0 104-34.213 104-80.5S185.438 36 128 36"/>
            </svg>
            카카오톡 문의
          </a>
          <button
            className="px-5 py-2 text-sm font-medium bg-white text-black rounded-full hover:bg-zinc-200 transition-all"
            onClick={() => {
              const contactSection = document.querySelector('#contact');
              contactSection?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            프로젝트 문의
          </button>
        </div>
      </NavBody>

      {/* Mobile Navigation */}
      <MobileNav>
        <MobileNavHeader className="px-4 bg-black">
          <a href="/" className="flex items-center">
            <span className="text-base font-semibold text-white">
              Nalda<span className="text-zinc-500">Dev</span>
            </span>
          </a>
          <MobileNavToggle
            isOpen={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          />
        </MobileNavHeader>

        <MobileNavMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          className="!rounded-2xl !shadow-2xl !mt-2"
          style={{
            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.8) 100%)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          {navItems.map((item, idx) => (
            <motion.a
              key={`mobile-link-${idx}`}
              href={item.link}
              onClick={() => setIsMobileMenuOpen(false)}
              className={cn(
                "w-full px-4 py-3 text-base font-medium rounded-xl transition-colors",
                "hover:bg-zinc-900",
                activeSection === item.link.slice(1)
                  ? "text-white bg-zinc-900"
                  : "text-zinc-400 hover:text-white"
              )}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              {item.name}
            </motion.a>
          ))}
          
          <div className="w-full h-px bg-zinc-800/50 my-2" />
          
          <motion.div 
            className="flex w-full flex-col gap-3 px-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <a
              href="http://pf.kakao.com/_XSxhNn/chat"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-full px-4 py-3 text-base font-medium text-zinc-400 hover:text-white bg-zinc-900/50 hover:bg-zinc-900 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 256 256" fill="currentColor">
                <path d="M128 36C70.562 36 24 72.713 24 118c0 29.279 19.466 54.97 48.748 69.477l-10.534 38.883c-.905 3.34 2.998 5.99 5.756 3.905l46.725-33.452C120.077 197.905 125.975 198.5 128 198.5c57.438 0 104-34.213 104-80.5S185.438 36 128 36"/>
              </svg>
              카카오톡 문의
            </a>
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                const contactSection = document.querySelector('#contact');
                contactSection?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="w-full px-4 py-3 text-base font-medium bg-white text-black rounded-xl hover:bg-zinc-200 transition-all"
            >
              프로젝트 문의
            </button>
          </motion.div>
        </MobileNavMenu>
      </MobileNav>
    </Navbar>
    </motion.div>
  );
};

export default Header;